// @flow

import * as React from 'react'
import { Route, Redirect, Switch } from 'react-router-dom'
import { ToastContainer, Flip } from 'react-toastify'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'

import Base from './layout/Base.layout'
import Accounts from './modules/accounts'
import Inventory from './modules/inventory'
import Invoices from './modules/invoices'
import Order from './modules/order/'
import Orders from './modules/orders/'
import Login from './modules/login/'
import HeaderContainer from './components/HeaderContainer'
import ActionAlerts from './components/ActionAlerts'
import { OrderContentProvider } from './context/orderContent.context'
import { setLicenseKey } from 'wijmo/wijmo'
import logger from './utils/logger'
import Coops from './modules/coops'
import SalesAnalysis from './modules/sales'
import Shipping from './modules/shipping'
import Samples from './modules/samples'
import {
  AnnouncementsContext,
  AnnouncementsProvider,
} from './context/announcements.context';
import { ActionsProvider } from './context/actions.context';
import { MobileView } from './config/mediaQuery'
import { HolidaysProvider } from './context/holidays.context';

logger.log(process.env)

// Regexes for transient network failures (wifi drop, VPN reconnect, tab-sleep,
// browser cancelled in-flight fetch). The Apollo onError link in Base.layout.js
// captures these as tagged warnings with full operation context; beforeSend
// below drops any duplicate auto-captures so we get exactly one clean record.
const TRANSIENT_NETWORK_ERROR_PATTERNS = [
  /Network error: Failed to fetch/i,
  /NetworkError when attempting to fetch resource/i,
  /^Failed to fetch$/i,
  /The network connection was lost/i,
  /^Load failed$/i,
  /The Internet connection appears to be offline/i,
]

const isTransientNetworkMessage = (msg) => {
  if (!msg || typeof msg !== 'string') return false
  return TRANSIENT_NETWORK_ERROR_PATTERNS.some((re) => re.test(msg))
}

const sentryKey = process.env.REACT_APP_SENTRY_KEY
if (sentryKey) {
  Sentry.init({
    dsn: sentryKey,

    release: `${process.env.REACT_APP_NAME}@${process.env.REACT_APP_VERSION}`,
    environment: process.env.REACT_APP_ENVIRONMENT,
    debug: false,

    integrations: [
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/default/#modifying-system-integrations
      new Sentry.Integrations.Breadcrumbs({
        console: false,
        fetch: true,
        xhr: true,
      }),
      new BrowserTracing({ tracingOrigins: ['*'] }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    denyUrls: ['//localhost'],
    ignoreErrors: [
      // Chrome extensions
      /runtime\.sendMessage/,
      /Extension context invalidated/,
      // Common benign browser errors
      /ResizeObserver loop/,
      // Generic non-Error rejections (often from extensions)
      /Non-Error promise rejection captured/,
    ],

    beforeSend(event, hint) {
      // 1. Filter out errors originating from browser extensions.
        const frames =
  (event.exception &&
    event.exception.values &&
    event.exception.values[0] &&
    event.exception.values[0].stacktrace &&
    event.exception.values[0].stacktrace.frames) ||
  []
	const isExtensionError = frames.some(
        (frame) =>
          frame.filename &&
          (frame.filename.startsWith('chrome-extension://') ||
            frame.filename.startsWith('moz-extension://') ||
            frame.filename.startsWith('safari-extension://'))
      )
      if (isExtensionError) {
        return null
      }

      // 2. Deduplicate Apollo network-error noise. The Apollo onError link
      // (client/src/layout/Base.layout.js) captures these as tagged warnings
      // with operation context; drop any other copies (unhandledrejection
      // auto-instrumentation, etc.) so each flake yields exactly one record.
      const err = hint && hint.originalException
      const message =
        (event && event.message) ||
        (err && err.message) ||
        (typeof err === 'string' ? err : '') ||
        ''

      if (isTransientNetworkMessage(message)) {
        const tag =
          (event && event.tags && event.tags['error.type']) || ''
        if (tag !== 'apollo_network_error') {
          return null
        }
      }

      return event
    },
  })

  // Silence browser-console spam from Apollo network-error rejections.
  // Sentry capture is already handled (as a warning with context) by the
  // Apollo onError link; this prevents the default
  // "Uncaught (in promise) Error: Network error: Failed to fetch" log.
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event && event.reason
      const message =
        (reason && (reason.message || (typeof reason === 'string' ? reason : ''))) || ''
      const hasNetworkError = !!(reason && reason.networkError)

      if (isTransientNetworkMessage(message) || hasNetworkError) {
        event.preventDefault()
      }
    })
  }

  // Uncomment to test integration with Sentry
  /*
    const transaction = Sentry.startTransaction({
      op: "test",
      name: "My First Test Transaction",
    });

    setTimeout(() => {
      try {
        throw new Error();
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        transaction.finish();
      }
    }, 99);
  */
}

setLicenseKey(process.env.REACT_APP_WIJMO_KEY)

export default class App extends React.Component<{}> {
  redirectToDefaultRoute = () => {
    return <Redirect to="/accounts" />
  }

  componentDidCatch(
    error: Error,
    errorInfo: {
      componentStack: string,
    },
  ) {
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo)

      Sentry.captureException(error)
    })
  }

  render() {
    return (
      <Base>
        {({ isLoggedIn } = {}) => {
          return (
            <AnnouncementsProvider>
              {!isLoggedIn ? (
                <Route path="/login">
                  {({ match }) => {
                    if (isLoggedIn === true) {
                      return <Redirect to="/accounts" />
                      // `match` will be truthy only if the current page is
                      // the actual login page
                    } else if (!match) {
                      return <Redirect to="/login" />
                    }

                    return <Login />
                  }}
                </Route>
              ) : (
                <>
                  <HolidaysProvider>
                    <OrderContentProvider>
                      <ActionsProvider>
                        <HeaderContainer />
                        <ToastContainer
                          position="top-right"
                          autoClose={7000}
                          pauseOnVisibilityChange
                          transition={Flip}
                        />
                        <AnnouncementsContext.Consumer>
                          {(value) => {
                            const message = value.getMessage();
                            const messageData = message ? [...message.data] : [];
                            return (
                              messageData &&
                              messageData.map(
                                ({ moduleName, anouncements }: any, i) => (
                                  <MobileView key={`${moduleName}-mobile${i}`}>
                                    {(isMobile) => (
                                      <ActionAlerts
                                        key={`${moduleName}-${i}`}
                                        moduleName={moduleName}
                                        anouncements={anouncements}
                                        isMobile={isMobile}
                                      />
                                    )}
                                  </MobileView>
                                ),
                              )
                            )
                          }}
                        </AnnouncementsContext.Consumer>
                        <Route
                          path="/"
                          exact
                          render={this.redirectToDefaultRoute}
                        />
                        <Route
                          path="/login"
                          exact
                          render={this.redirectToDefaultRoute}
                        />
                        <Route
                          path="/accounts"
                          children={(props) => {
                            const { match } = props

                            return <Accounts isFullPage active={match !== null} />
                          }}
                        />
                        <Route
                          path="/inventory"
                          children={(props) => {
                            const { match } = props

                            return (
                              <Inventory isFullPage active={match !== null} />
                            )
                          }}
                        />
                        <Route
                          path="/invoices"
                          children={(props) => {
                            const { match } = props

                            return <Invoices isFullPage active={match !== null} />
                          }}
                        />
                        <Route
                          path="/orders"
                          children={(props) => {
                            const { match } = props

                            return <Orders isFullPage active={match !== null} />
                          }}
                        />
                        <Route
                          path="/coops"
                          children={(props) => {
                            const { match } = props

                            return <Coops isFullPage active={match !== null} />
                          }}
                        />
                        <Route
                          path="/shipping"
                          children={(props) => {
                            const { match } = props

                            return <Shipping isFullPage active={match !== null} />
                          }}
                        />
                        <Route
                          path="/sales analysis"
                          children={(props) => {
                            const { match } = props

                            return (
                              <SalesAnalysis isFullPage active={match !== null} />
                            )
                          }}
                        />
                        <Route
                          path="/samples"
                          children={(props) => {
                            const { match } = props

                            return <Samples isFullPage active={match !== null} />
                          }}
                        />
                        <Switch>
                          <Route
                            path="/order"
                            children={(props) => {
                              const { match } = props

                              return (
                                <Order
                                  isFullPage
                                  {...props}
                                  active={match !== null}
                                />
                              )
                            }}
                          />
                        </Switch>
                      </ActionsProvider>
                    </OrderContentProvider>
                  </HolidaysProvider>
                </>
              )}
            </AnnouncementsProvider>
          )
        }}
      </Base>
    )
  }
}
